import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime } from 'rxjs';

import { EditTemplateService } from '../../services/edit-template/edit-template.service';
import { UploadFileManagerService } from '../../services/upload-file-manager/upload-file-manager.service';
import columnDefs from '../../utils/grid-column-defs/edit-template.column-defs';
import isKeyInObject from '../../utils/is-key-in-object/is-key-in-object';
import { GridComponent } from '../grid/grid/grid.component';

@Component({
  selector: 'app-edit-template-form-table-host',
  templateUrl: './edit-template-form-table-host.component.html',
  styleUrls: ['./edit-template-form-table-host.component.scss'],
  imports: [ReactiveFormsModule, GridComponent],
})
export class EditTemplateFormTableHostComponent implements OnInit, OnChanges {
  /* allColumnsFilterName is the name for toolbar filter dropdown 'All' option */
  allColumnsFilterName = 'All columns';
  @Input() isTemplateViewReady = false;

  columnDefs = columnDefs;
  /* columnNames extracted from columnDefs for dropdown filter */
  columnNames: string[] = [];
  /* filterForm for the top toolbar filter functionality */
  filterForm = new FormGroup({
    searchString: new FormControl('', {
      nonNullable: true,
    }),
    columnName: new FormControl(this.allColumnsFilterName, {
      nonNullable: true,
    }),
  });

  constructor(
    readonly editTemplateService: EditTemplateService,
    private uploadService: UploadFileManagerService,
  ) {}

  /**
   * getColumnNamesForFilter() will iterate over the column keys to get the propNames for
   * the filter dropdown - subject to change with UX improvements
   */
  getColumnNamesForFilter() {
    Object.keys(this.columnDefs).forEach(colDefKey => {
      this.columnNames.push(this.columnDefs[colDefKey].prop);
    });
  }

  /**
   * toggleFilterFormDisabled() will enable or disable filter inputs based on
   * input boolean from the parent component that indicates if the view is ready
   */
  toggleFilterFormDisabled(): void {
    if (this.isTemplateViewReady === true) return this.filterForm.enable();
    return this.filterForm.disable();
  }

  /**
   * watchFormValues() will trigger relevant functions if the toolbar filter
   * input values change
   */
  watchFormValues() {
    this.filterForm.valueChanges.pipe(debounceTime(150)).subscribe(() => {
      this.filterTemplateInputList();
    });
  }

  /**
   * filterTemplateInputList() is a part of the UX improvements and is a subject to change,
   * in current form it will filter the input fields detected by PDF viewer based on
   * the input search string and the column name chosen
   */
  filterTemplateInputList() {
    const columnName = this.filterForm.controls.columnName.value;
    const searchString = this.filterForm.controls.searchString.value.trim().toLowerCase();

    this.editTemplateService.filteredTempJson = this.editTemplateService.temporaryJson.filter(
      fieldObject => {
        if (columnName === this.allColumnsFilterName)
          return (
            fieldObject.ID.toLowerCase().includes(searchString) ||
            fieldObject.FIELDNAME.toLowerCase().includes(searchString) ||
            fieldObject.FIELDTYPE.toLowerCase().includes(searchString) ||
            fieldObject.SUBTYPE.toLowerCase().includes(searchString) ||
            fieldObject.DESCRIPTION?.toLowerCase().includes(searchString) ||
            fieldObject.BOFIELDNAME?.toLowerCase().includes(searchString)
          );
        if (isKeyInObject(columnName, fieldObject))
          return JSON.stringify(fieldObject[columnName])?.toLowerCase().includes(searchString);
        return;
      },
    );
  }

  /**
   * uploadMappings()
   * will post the annotations pulled from the PDF and edited by the user
   * in the JSON format to the Document mappings endpoint
   */
  uploadMappings() {
    this.editTemplateService.uploadingTemplateState = true;
    this.uploadService.uploadMappings();
  }

  ngOnInit(): void {
    this.getColumnNamesForFilter();
    this.watchFormValues();
    this.toggleFilterFormDisabled();
  }

  ngOnChanges(): void {
    this.toggleFilterFormDisabled();
  }
}
